import { useState, useEffect } from 'react';
import {
    UserPlus,
    ArrowLeft,
    Mail,
    Layers,
    ShieldCheck,
    CheckCircle,
    Info,
    AlertCircle,
    Phone,
    GraduationCap,
    BookOpen,
    CreditCard,
    DollarSign,
    Clock,
    ExternalLink,
    Calendar
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function AddStudent() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const cohortIdFromUrl = searchParams.get('cohortId');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        course_name: '',
        education_level: "Bachelor's Degree",
        payment_plan: 'full', // 'full' (100%) or 'installment' (50%)
        payment_method: 'manual',
        custom_payment_link: '',
        custom_payment_amount: '' as string | number,
        payment_deadline: '',
        cohorts: cohortIdFromUrl ? [cohortIdFromUrl] : [] as string[],
        sendWelcomeEmail: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [cohorts, setCohorts] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const fetchCohorts = async () => {
        try {
            const response = await fetch(`${API_URL}/cohorts`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (response.ok) {
                setCohorts(data);
                if (cohortIdFromUrl && data.length > 0) {
                    const matched = data.find((c: any) => String(c.id) === String(cohortIdFromUrl));
                    if (matched) {
                        setFormData(prev => ({ ...prev, course_name: matched.name || matched.course?.title || prev.course_name }));
                    }
                }
            }
        } catch (err) {
            console.error('Failed to load academic sessions.');
        }
    };

    useEffect(() => {
        fetchCohorts();
    }, []);

    const toggleCohort = (id: string | number) => {
        setFormData(prev => {
            const idStr = String(id);
            const isSelected = prev.cohorts.some(c => String(c) === idStr);
            const selected = isSelected
                ? prev.cohorts.filter(c => String(c) !== idStr)
                : [...prev.cohorts, idStr];

            // Set course_name to the exact cohort selected by the instructor
            let nextCourseName = prev.course_name;
            if (!isSelected) {
                const found = cohorts.find(c => String(c.id) === idStr);
                if (found) {
                    nextCourseName = found.name;
                }
            } else if (selected.length > 0) {
                const remaining = cohorts.find(c => String(c.id) === String(selected[selected.length - 1]));
                if (remaining) {
                    nextCourseName = remaining.name;
                }
            } else {
                nextCourseName = '';
            }

            return { ...prev, cohorts: selected, course_name: nextCourseName };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            const isInstallment = formData.payment_plan === 'installment';

            let selectedCourseName = formData.course_name ? formData.course_name.trim() : '';
            if (!selectedCourseName && formData.cohorts.length > 0) {
                const matchedCohort = cohorts.find(c => String(c.id) === String(formData.cohorts[0]));
                if (matchedCohort) {
                    selectedCourseName = matchedCohort.name;
                }
            }

            const payload = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone || null,
                course_name: selectedCourseName || null,
                cohort_name: selectedCourseName || null,
                education_level: formData.education_level,
                payment_plan: formData.payment_plan,
                payment_method: formData.payment_method,
                custom_payment_link: formData.custom_payment_link ? formData.custom_payment_link.trim() : null,
                custom_payment_amount: formData.custom_payment_amount !== '' && formData.custom_payment_amount !== null ? Number(formData.custom_payment_amount) : null,
                payment_deadline: formData.payment_deadline ? (formData.payment_deadline.includes('T') ? formData.payment_deadline.replace('T', ' ') : formData.payment_deadline) : null,
                payment_status: 'approved',
                payment_tracking_enabled: isInstallment,
                cohorts: formData.cohorts,
                password: formData.password || undefined,
                send_welcome_email: formData.sendWelcomeEmail
            };

            let response = await fetch(`${API_URL}/instructor/students`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 404) {
                response = await fetch(`${API_URL}/students`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();

            if (response.ok) {
                setIsSuccess(true);
                setTimeout(() => {
                    navigate('/instructor/students');
                }, 1800);
            } else {
                throw new Error(data.message || 'Enrollment rejected by central registry.');
            }
        } catch (err: any) {
            setError(err.message);
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="add-student-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                <div style={{ textAlign: 'center', animation: 'scaleIn 0.5s ease-out' }}>
                    <div style={{ width: '100px', height: '100px', background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)', borderRadius: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2.5rem', color: 'var(--index-primary-color)' }}>
                        <CheckCircle size={50} />
                    </div>
                    <h2 style={{ fontSize: '2.5rem', fontWeight: 950, color: 'var(--index-text-heading)', margin: '0 0 1rem 0' }}>Student Enrolled</h2>
                    <p style={{ color: 'var(--index-text-secondary)', fontSize: '1.2rem', fontWeight: 600 }}>The student profile has been created and verified successfully. Redirecting...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="add-student-container">
            <style>{`
                .staff-scope .add-student-container {
                    max-width: 1050px;
                    margin: 0 auto;
                    font-family: 'Inter', system-ui, -apple-system, sans-serif;
                }

                .staff-scope .back-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: var(--index-text-secondary);
                    text-decoration: none;
                    font-weight: 800;
                    font-size: 0.9rem;
                    margin-bottom: 2.5rem;
                    transition: color 0.3s;
                }

                .staff-scope .back-link:hover {
                    color: var(--index-text-heading);
                }

                .staff-scope .registration-layout {
                    display: grid;
                    grid-template-columns: 1.25fr 0.75fr;
                    gap: 2.5rem;
                }

                .staff-scope .form-card-premium {
                    background: var(--index-card-bg);
                    border: 1.5px solid var(--index-border-color);
                    border-radius: 32px;
                    padding: 2.75rem;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.02);
                }

                .staff-scope .section-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 1.75rem;
                }

                .staff-scope .icon-box {
                    background: var(--index-hover-bg);
                    padding: 10px;
                    border-radius: 12px;
                    color: var(--index-primary-color);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .staff-scope .section-title h2 {
                    margin: 0;
                    font-size: 1.35rem;
                    font-weight: 950;
                    color: var(--index-text-heading);
                }

                .staff-scope .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.25rem;
                    margin-bottom: 2rem;
                }

                .staff-scope .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .staff-scope .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .staff-scope .form-group label {
                    font-weight: 850;
                    color: var(--index-text-heading);
                    font-size: 0.88rem;
                    letter-spacing: -0.01em;
                }

                .staff-scope .input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .staff-scope .input-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--index-text-faint);
                    pointer-events: none;
                    z-index: 2;
                }

                .staff-scope .premium-input,
                .staff-scope .premium-select {
                    width: 100%;
                    padding: 0.85rem 1.25rem 0.85rem 3rem;
                    background-color: var(--index-card-bg);
                    border: 1.5px solid var(--index-border-color);
                    border-radius: 16px;
                    font-family: inherit;
                    font-size: 0.95rem;
                    font-weight: 600;
                    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
                    color: var(--index-text-heading);
                    box-sizing: border-box;
                }

                .staff-scope .premium-select {
                    cursor: pointer;
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-9'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 1.25rem center;
                    background-size: 16px;
                    padding-right: 2.75rem;
                }

                :root.dark .staff-scope .premium-input,
                .dark .staff-scope .premium-input,
                :root.dark .staff-scope .premium-select,
                .dark .staff-scope .premium-select {
                    background-color: #282a3a !important;
                    color: #ffffff !important;
                    border-color: #36384a !important;
                }

                :root.dark .staff-scope .premium-select,
                .dark .staff-scope .premium-select {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-9'/%3E%3C/svg%3E") !important;
                    background-repeat: no-repeat !important;
                    background-position: right 1.25rem center !important;
                    background-size: 16px !important;
                }

                :root.dark .admin-mode .staff-scope .premium-select,
                .dark .admin-mode .staff-scope .premium-select,
                .admin-mode.dark .staff-scope .premium-select {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23f87171' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-9'/%3E%3C/svg%3E") !important;
                }

                .staff-scope .premium-select option,
                .staff-scope select option {
                    background-color: #ffffff;
                    color: #1E1F2B;
                    padding: 12px 14px;
                }

                :root.dark .staff-scope .premium-select option,
                :root.dark .staff-scope select option,
                .dark .staff-scope .premium-select option,
                .dark .staff-scope select option,
                :root.dark select option,
                .dark select option {
                    background-color: #1E1F2B !important;
                    color: #ffffff !important;
                    padding: 12px 14px;
                }

                .staff-scope .premium-input:focus,
                .staff-scope .premium-select:focus,
                .premium-input:focus {
                    outline: none;
                    border-color: var(--index-primary-color) !important;
                    box-shadow: 0 0 0 4px color-mix(in srgb, var(--index-primary-color) calc(0.15 * 100%), transparent) !important;
                }

                .staff-scope .plan-cards-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .staff-scope .plan-card {
                    padding: 1.25rem;
                    border-radius: 18px;
                    border: 2px solid var(--index-border-color);
                    background: var(--index-card-bg);
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .staff-scope .plan-card:hover {
                    border-color: color-mix(in srgb, var(--index-primary-color) 40%, transparent);
                    background: var(--index-hover-bg);
                }

                .staff-scope .plan-card.selected {
                    border-color: var(--index-primary-color);
                    background: color-mix(in srgb, var(--index-primary-color) 12%, transparent);
                }

                .staff-scope .plan-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .staff-scope .plan-title {
                    font-weight: 900;
                    font-size: 0.95rem;
                    color: var(--index-text-heading);
                }

                .staff-scope .plan-desc {
                    font-size: 0.78rem;
                    color: var(--index-text-secondary);
                    font-weight: 600;
                    line-height: 1.4;
                }

                .staff-scope .cohort-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 0.75rem;
                }

                .staff-scope .cohort-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 1rem;
                    background: var(--index-card-bg);
                    border: 1.5px solid var(--index-border-color);
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .staff-scope .cohort-item:hover {
                    border-color: var(--index-primary-color);
                    background: var(--index-hover-bg);
                }

                .staff-scope .cohort-item.selected {
                    background: color-mix(in srgb, var(--lgl-success) 14%, transparent);
                    border-color: var(--index-primary-color);
                }

                .staff-scope .cohort-checkbox {
                    width: 20px;
                    height: 20px;
                    accent-color: var(--index-primary-color);
                }

                .staff-scope .cohort-info {
                    display: flex;
                    flex-direction: column;
                }

                .staff-scope .cohort-name {
                    font-weight: 850;
                    color: var(--index-text-heading);
                    font-size: 0.9rem;
                }

                .staff-scope .cohort-course {
                    font-size: 0.75rem;
                    color: var(--index-text-secondary);
                    font-weight: 600;
                }

                .staff-scope .sidebar-info {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .staff-scope .info-card {
                    background: var(--index-card-bg);
                    border-radius: 24px;
                    padding: 1.75rem;
                    border: 1.5px solid var(--index-border-color);
                }

                .info-card h4 {
                    margin: 0 0 1rem 0;
                    font-size: 1rem;
                    font-weight: 900;
                    color: var(--index-text-heading);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .staff-scope .info-item {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 1rem;
                }

                .staff-scope .info-item-text {
                    font-size: 0.85rem;
                    color: var(--index-text-secondary);
                    font-weight: 600;
                    line-height: 1.5;
                }

                .staff-scope .btn-submit {
                    background: var(--index-primary-color);
                    color: white;
                    border: none;
                    padding: 1rem 2rem;
                    border-radius: 18px;
                    font-weight: 950;
                    font-size: 1rem;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    transition: all 0.3s;
                    width: 100%;
                    margin-top: 1rem;
                }

                .staff-scope .btn-submit:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px -5px color-mix(in srgb, var(--index-primary-color) calc(0.3 * 100%), transparent);
                }

                .staff-scope .btn-submit:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                    transform: none;
                }

                @media (max-width: 850px) {
                    .staff-scope .registration-layout {
                        grid-template-columns: 1fr;
                    }
                    .staff-scope .form-grid {
                        grid-template-columns: 1fr;
                    }
                    .staff-scope .plan-cards-grid {
                        grid-template-columns: 1fr;
                    }
                }

                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>

            <button onClick={() => navigate(-1)} className="back-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <ArrowLeft size={18} /> Back to Students
            </button>

            {error && (
                <div style={{ padding: '1.25rem', background: 'var(--index-danger-bg-soft)', border: '1.5px solid var(--index-danger-bg-soft)', borderRadius: '18px', color: 'var(--lgl-error)', fontWeight: 700, marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <AlertCircle size={20} />
                    {error}
                </div>
            )}

            <div className="registration-layout">
                <div className="form-card-premium shadow-premium">
                    <form onSubmit={handleSubmit}>
                        {/* Section 1: Basic Information */}
                        <div className="section-title">
                            <div className="icon-box"><UserPlus size={20} /></div>
                            <h2>Student Information</h2>
                        </div>

                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label>Full Name *</label>
                                <div className="input-wrapper">
                                    <UserPlus className="input-icon" size={18} />
                                    <input
                                        type="text"
                                        className="premium-input"
                                        placeholder="e.g. Jane Doe"
                                        required
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Email Address *</label>
                                <div className="input-wrapper">
                                    <Mail className="input-icon" size={18} />
                                    <input
                                        type="email"
                                        className="premium-input"
                                        placeholder="student@example.com"
                                        required
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Phone Number</label>
                                <div className="input-wrapper">
                                    <Phone className="input-icon" size={18} />
                                    <input
                                        type="tel"
                                        className="premium-input"
                                        placeholder="+1 (555) 000-0000"
                                        value={formData.phone}
                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label>Temporary Password (Optional)</label>
                                <div className="input-wrapper">
                                    <ShieldCheck className="input-icon" size={18} />
                                    <input
                                        type="text"
                                        className="premium-input"
                                        placeholder="Defaults to 'password123' if left blank"
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Course Track & Academic Background */}
                        <div className="section-title" style={{ marginTop: '1.5rem' }}>
                            <div className="icon-box"><BookOpen size={20} /></div>
                            <h2>Academic Track & Education</h2>
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label>Course / Program Track *</label>
                                <div className="input-wrapper">
                                    <BookOpen className="input-icon" size={18} />
                                    <select
                                        className="premium-select"
                                        value={formData.course_name}
                                        onChange={e => {
                                            const selectedVal = e.target.value;
                                            const matched = cohorts.find((c: any) => c.name === selectedVal || String(c.id) === selectedVal);
                                            setFormData(prev => ({
                                                ...prev,
                                                course_name: selectedVal,
                                                cohorts: matched ? [matched.id] : prev.cohorts
                                            }));
                                        }}
                                    >
                                        <option value="">-- Choose Cohort or Academic Track --</option>
                                        {cohorts.length > 0 && (
                                            <optgroup label="Academic Cohorts">
                                                {cohorts.map((c: any) => (
                                                    <option key={c.id} value={c.name}>
                                                        {c.name} {c.course?.title ? `• ${c.course.title}` : ''}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                        <optgroup label="Academic Programs">
                                            <option value="Project Scheduling & Controls Training">Project Scheduling & Controls Training</option>
                                            <option value="All-in-One Dual Masterclass Bundle">All-in-One Dual Masterclass Bundle</option>
                                            <option value="Professional Masterclass">Professional Masterclass</option>
                                            <option value="Foundation Academy">Foundation Academy</option>
                                            <option value="Cybersecurity Analyst Track">Cybersecurity Analyst Track</option>
                                            <option value="Cloud Engineering Track">Cloud Engineering Track</option>
                                            <option value="Custom Program">Custom Program</option>
                                        </optgroup>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Education Level</label>
                                <div className="input-wrapper">
                                    <GraduationCap className="input-icon" size={18} />
                                    <select
                                        className="premium-select"
                                        value={formData.education_level}
                                        onChange={e => setFormData({ ...formData, education_level: e.target.value })}
                                    >
                                        <option value="High School Diploma">High School Diploma / GED</option>
                                        <option value="Associate Degree">Associate Degree</option>
                                        <option value="Bachelor's Degree">Bachelor's Degree</option>
                                        <option value="Master's Degree">Master's Degree</option>
                                        <option value="Doctorate">Doctorate / PhD</option>
                                        <option value="Professional Certification">Professional Certification</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Payment Plan & Reminder Configuration */}
                        <div className="section-title" style={{ marginTop: '1.5rem' }}>
                            <div className="icon-box"><DollarSign size={20} /></div>
                            <h2>Payment Plan & Tuition Tracking</h2>
                        </div>

                        <div className="plan-cards-grid">
                            <div
                                className={`plan-card ${formData.payment_plan === 'full' ? 'selected' : ''}`}
                                onClick={() => setFormData({ ...formData, payment_plan: 'full' })}
                            >
                                <div className="plan-header">
                                    <span className="plan-title">100% Full Payment</span>
                                    <CreditCard size={18} color={formData.payment_plan === 'full' ? 'var(--index-primary-color)' : 'var(--index-text-faint)'} />
                                </div>
                                <p className="plan-desc">
                                    Fully paid upfront. No remaining tuition reminder countdown or access locks will be applied.
                                </p>
                            </div>

                            <div
                                className={`plan-card ${formData.payment_plan === 'installment' ? 'selected' : ''}`}
                                onClick={() => setFormData({ ...formData, payment_plan: 'installment' })}
                            >
                                <div className="plan-header">
                                    <span className="plan-title" style={{ color: formData.payment_plan === 'installment' ? 'var(--lgl-warning)' : 'inherit' }}>
                                        50% Installment Payment
                                    </span>
                                    <Clock size={18} color={formData.payment_plan === 'installment' ? 'var(--lgl-warning)' : 'var(--index-text-faint)'} />
                                </div>
                                <p className="plan-desc">
                                    Part payment (50% down). <strong>Activates tuition deadline countdown reminder</strong> on student dashboard.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label>Payment Method</label>
                                <div className="input-wrapper">
                                    <CreditCard className="input-icon" size={18} />
                                    <select
                                        className="premium-select"
                                        value={formData.payment_method}
                                        onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
                                    >
                                        <option value="manual">Manual / Instructor Verified (Direct Deposit / Offline)</option>
                                        <option value="zelle">Zelle</option>
                                        <option value="square">Square</option>
                                        <option value="stripe">Stripe</option>
                                        <option value="bank_transfer">Bank Wire / Transfer</option>
                                        <option value="cash">Cash</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Custom 50% Balance Payment Link</label>
                                <div className="input-wrapper">
                                    <ExternalLink className="input-icon" size={18} />
                                    <input
                                        type="url"
                                        className="premium-input"
                                        placeholder="e.g. https://square.link/u/... (Leave blank for default flow)"
                                        value={formData.custom_payment_link}
                                        onChange={e => setFormData({ ...formData, custom_payment_link: e.target.value })}
                                    />
                                </div>
                                <span style={{ fontSize: '0.75rem', color: 'var(--index-text-secondary)', marginTop: '4px', display: 'block' }}>
                                    Optional Square/Stripe checkout URL override.
                                </span>
                            </div>

                            <div className="form-group">
                                <label>Custom 50% Balance Amount ($ USD)</label>
                                <div className="input-wrapper">
                                    <DollarSign className="input-icon" size={18} />
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="premium-input"
                                        placeholder="e.g. 600, 900, 500 (Leave blank for default)"
                                        value={formData.custom_payment_amount}
                                        onChange={e => setFormData({ ...formData, custom_payment_amount: e.target.value })}
                                    />
                                </div>
                                <span style={{ fontSize: '0.75rem', color: 'var(--index-text-secondary)', marginTop: '4px', display: 'block' }}>
                                    Exact 50% balance amount that shows on the student's Payment Access Tracker.
                                </span>
                            </div>

                            <div className="form-group">
                                <label>Custom Expiration / Payment Deadline Date & Time (Central Time - CST)</label>
                                <div className="input-wrapper">
                                    <Calendar className="input-icon" size={18} />
                                    <input
                                        type="datetime-local"
                                        className="premium-input"
                                        value={formData.payment_deadline}
                                        onChange={e => setFormData({ ...formData, payment_deadline: e.target.value })}
                                    />
                                </div>
                                <span style={{ fontSize: '0.75rem', color: 'var(--index-text-secondary)', marginTop: '4px', display: 'block' }}>
                                    Custom payment tracker deadline for this student only in Central Time (CST). Leave blank to inherit standard course/cohort expiration.
                                </span>
                            </div>
                        </div>

                        {/* Section 4: Cohort Assignment */}
                        <div className="section-title" style={{ marginTop: '1.5rem' }}>
                            <div className="icon-box"><Layers size={20} /></div>
                            <h2>Assign Cohort(s)</h2>
                        </div>

                        <div className="cohort-grid" style={{ marginBottom: '2rem' }}>
                            {cohorts.length > 0 ? (
                                cohorts.map(c => {
                                    const isChecked = formData.cohorts.some(id => String(id) === String(c.id));
                                    return (
                                        <div
                                            key={c.id}
                                            className={`cohort-item ${isChecked ? 'selected' : ''}`}
                                            onClick={() => toggleCohort(c.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                className="cohort-checkbox"
                                                checked={isChecked}
                                                onChange={() => { }} // Handled by div onClick
                                            />
                                            <div className="cohort-info">
                                                <span className="cohort-name">{c.name}</span>
                                                <span className="cohort-course">{c.course?.title || 'No Course Attached'}</span>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p style={{ color: 'var(--index-text-secondary)', fontSize: '0.9rem' }}>No cohorts available.</p>
                            )}
                        </div>

                        {/* Section 5: Email Notification */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--index-hover-bg)', padding: '1.25rem', borderRadius: '18px', marginBottom: '2.5rem', border: '1.5px solid var(--index-border-color)' }}>
                            <input
                                type="checkbox"
                                id="welcomeEmail"
                                checked={formData.sendWelcomeEmail}
                                onChange={e => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                                style={{ width: '20px', height: '20px', accentColor: 'var(--index-primary-color)' }}
                            />
                            <label htmlFor="welcomeEmail" style={{ fontSize: '0.9rem', color: 'var(--index-text-heading)', fontWeight: 750, cursor: 'pointer' }}>
                                Send welcome email with login credentials and access details.
                            </label>
                        </div>

                        <button type="submit" className="btn-submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Enrolling Student...' : 'Create & Verify Student Profile'}
                        </button>
                    </form>
                </div>

                <div className="sidebar-info">
                    <div className="info-card shadow-premium">
                        <h4><ShieldCheck size={18} color="var(--index-primary-color)" /> Instant Verification</h4>
                        <div className="info-item">
                            <div className="info-item-text">
                                When added manually by an instructor, the student's status is automatically set to <strong>Approved</strong>. No pending manual review is required.
                            </div>
                        </div>
                    </div>

                    <div className="info-card shadow-premium">
                        <h4><Clock size={18} color="var(--lgl-warning)" /> 50% Reminder Automation</h4>
                        <div className="info-item">
                            <div className="info-item-text">
                                Selecting <strong>50% Installment</strong> flags the student's account with tuition tracking. The student dashboard will automatically show the remaining balance countdown and payment link.
                            </div>
                        </div>
                    </div>

                    <div className="info-card shadow-premium">
                        <h4><Info size={18} color="var(--index-text-heading)" /> Credentials & Access</h4>
                        <div className="info-item">
                            <CheckCircle size={16} color="var(--index-primary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div className="info-item-text">The student can log in right away and access their assigned cohorts.</div>
                        </div>
                        <div className="info-item" style={{ marginBottom: 0 }}>
                            <CheckCircle size={16} color="var(--index-primary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div className="info-item-text">You can resend login credentials at any time from their profile.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

